import { useEffect, useMemo, useState } from "react";
import { TopicTable } from "../components/TopicTable";
import { Find } from "./Find";
import { copy } from "../copy";
import { matchesQuery, statusCounts, topicsForAccount } from "../domain/selectors";
import { isTopicStatus } from "../domain/status";
import { sortByRank, tasksForAccount } from "../domain/tasks";
import { todayInShanghai } from "../domain/time";
import { plainText } from "../lib/text";
import { useApp } from "../state/store";
import { TOPIC_STATUSES, type Topic, type TopicStatus } from "../types";

export function Inbox() {
  const app = useApp();
  const finding = new URLSearchParams(app.href.split("?")[1] ?? "").get("tab") === "find";
  const filter = filterFromHref(app.href);
  const due = dueFromHref(app.href);
  const today = todayInShanghai();
  const taskId = taskFromHref(app.href);
  const findTasks = tasksForAccount(app.state.tasks ?? [], app.accountId).filter((task) => task.type === "找选题");
  const topics = topicsForAccount(app.state.topics, app.accountId).filter((topic) => matchesQuery(topic, app.query) && (!taskId || topic.sourceTaskId === taskId));
  const counts = statusCounts(topics);
  const byStatus = filter === "all" ? topics : topics.filter((topic) => topic.status === filter);
  const matched = byStatus.filter((topic) => matchesDue(topic, due, today));
  const visible = filter === "待确认" || taskId ? sortByRank(matched) : matched;
  const [cursorId, setCursorId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const decision = filter === "待确认" || filter === "all";

  const firstId = visible[0]?.id ?? "";
  useEffect(() => {
    setSelected(new Set());
    setCursorId(firstId || null);
  }, [filter, app.query, firstId]);

  useEffect(() => {
    if (finding || (filter !== "待确认" && filter !== "all")) return;
    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return;
      const key = event.key.toLowerCase();
      if (key !== "j" && key !== "k" && key !== "d" && key !== "x") return;
      event.preventDefault();
      if (key === "j" || key === "k") {
        setCursorId((current) => move(visible, current, key === "j" ? 1 : -1));
        return;
      }
      const picked = selected.size
        ? visible.filter((topic) => selected.has(topic.id) && topic.status === "待确认")
        : visible.filter((topic) => topic.id === cursorId && topic.status === "待确认");
      if (!picked.length) return;
      decide(app, picked, key === "d" ? "已确认" : "不做");
      setSelected(new Set());
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [app, cursorId, filter, finding, selected, visible]);

  const tabs = useMemo(() => {
    const items: Array<{ id: TopicStatus | "all"; label: string; count: number }> = [
      { id: "all", label: copy.library.all, count: topics.length },
    ];
    for (const status of TOPIC_STATUSES) items.push({ id: status, label: status, count: counts[status] });
    return items;
  }, [counts, topics.length]);

  return (
    <div className="stack">
      <div className="status-tabs" role="tablist" aria-label={copy.pages.inbox.title}>
        <button type="button" role="tab" aria-selected={!finding} onClick={() => app.navigate("inbox", librarySearch(app.href))}>
          {copy.pages.inbox.title}
        </button>
        <button type="button" role="tab" aria-selected={finding} onClick={() => app.navigate("inbox", "tab=find")}>
          {copy.find.open}
        </button>
      </div>
      {finding ? <Find /> : null}
      {finding ? null : <div className="library-bar">
        {findTasks.length ? (
          <label className="task-filter">
            {copy.recommend.task}
            <select
              value={taskId}
              aria-label={copy.recommend.task}
              onChange={(event) => {
                const params = new URLSearchParams(app.href.split("?")[1] ?? "");
                params.delete("tab");
                if (event.target.value) params.set("task", event.target.value);
                else params.delete("task");
                if (filter !== "all") params.set("status", filter);
                app.navigate("inbox", params.toString());
              }}
            >
              <option value="">{copy.recommend.allTasks}</option>
              {findTasks.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.direction || task.type} · {task.status}
                </option>
              ))}
            </select>
          </label>
        ) : <span />}
      </div>}
      {finding ? null : <>
      <div className="status-tabs" role="tablist" aria-label="状态">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={filter === tab.id}
            onClick={() => app.navigate("inbox", tab.id === "all" ? librarySearch(app.href, "") : librarySearch(app.href, tab.id))}
          >
            {tab.label}
            <em>{tab.count}</em>
          </button>
        ))}
      </div>
      {decision ? <p className="muted key-hint">{copy.decide.hint}</p> : null}
      {selected.size ? (
        <div className="batch-bar">
          <span>{copy.decide.selected(selected.size)}</span>
          <button type="button" className="button line" onClick={() => applySelected("已确认")}>
            {copy.doIt}
          </button>
          <button type="button" className="button ghost" onClick={() => applySelected("不做")}>
            {copy.skip}
          </button>
        </div>
      ) : null}
      {visible.length === 0 ? (
        <section className="panel empty-card">
          <h2>{copy.library.empty}</h2>
          {filter !== "all" ? (
            <button type="button" className="button line" onClick={() => app.navigate("inbox")}>{copy.library.back}</button>
          ) : null}
        </section>
      ) : (
        <section className="panel table-panel">
          <TopicTable
            topics={visible}
            selected={selected}
            cursorId={cursorId}
            showStatus={filter === "all"}
            onToggle={(id) => {
              setSelected((current) => toggle(current, id));
              setCursorId(id);
            }}
            onToggleAll={(checked) => setSelected(checked ? new Set(visible.filter((topic) => topic.status === "待确认").map((topic) => topic.id)) : new Set())}
            onOpen={(topic) => app.openTopic(topic.id)}
            onDecide={(topic, status) => decide(app, [topic], status)}
          />
        </section>
      )}
      </>}
    </div>
  );

  function applySelected(status: "已确认" | "不做") {
    const picked = visible.filter((topic) => selected.has(topic.id) && topic.status === "待确认");
    if (!picked.length) return;
    decide(app, picked, status);
    setSelected(new Set());
  }
}

function librarySearch(href: string, status?: string): string {
  const params = new URLSearchParams(href.split("?")[1] ?? "");
  params.delete("tab");
  if (status !== undefined) {
    if (status) params.set("status", status);
    else params.delete("status");
  }
  return params.toString();
}

function filterFromHref(href: string): TopicStatus | "all" {
  const value = new URLSearchParams(href.split("?")[1] ?? "").get("status");
  return value && isTopicStatus(value) ? value : "all";
}

function taskFromHref(href: string): string {
  return new URLSearchParams(href.split("?")[1] ?? "").get("task") ?? "";
}

function dueFromHref(href: string): "overdue" | "today" | null {
  const value = new URLSearchParams(href.split("?")[1] ?? "").get("due");
  return value === "overdue" || value === "today" ? value : null;
}

function matchesDue(topic: Topic, due: "overdue" | "today" | null, today: string): boolean {
  if (!due) return true;
  if (!topic.plannedPublishDate || topic.status === "已完成" || topic.status === "不做") return false;
  if (due === "today") return topic.plannedPublishDate === today && topic.status === "待发布";
  return topic.plannedPublishDate < today;
}

function toggle(current: Set<string>, id: string): Set<string> {
  const next = new Set(current);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

function move(topics: Topic[], cursorId: string | null, delta: number): string | null {
  if (!topics.length) return null;
  const index = topics.findIndex((topic) => topic.id === cursorId);
  const start = index < 0 ? 0 : index;
  const next = topics[Math.min(topics.length - 1, Math.max(0, start + delta))];
  if (next) document.querySelector(`[data-topic-id="${CSS.escape(next.id)}"]`)?.scrollIntoView({ block: "nearest" });
  return next?.id ?? null;
}

function isTyping(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && Boolean(target.closest("input, textarea, select"));
}

function decide(app: ReturnType<typeof useApp>, topics: Topic[], status: "已确认" | "不做") {
  const undo = topics.map((topic) => ({ topicId: topic.id, status: topic.status }));
  for (const topic of topics) app.setStatus(topic.id, status);
  const message = topics.length === 1
    ? status === "已确认"
      ? copy.status.confirmed(plainText(topics[0]?.title))
      : copy.status.skipped(plainText(topics[0]?.title))
    : copy.decide.batch(topics.length, status);
  app.notify(message, "info", { label: copy.undo, undo });
}
